import { CoreoAttribute, CoreoRecord, CoreoRecordAttachment, CoreoRecordSyncStatus } from '../../types';

const nextClientAttachmentId = (): number => {
  return Number(
    Array.from(crypto.getRandomValues(new Uint8Array(13)), (byte) => byte % 10).join('')
  );
};

export const cloneRecordAttachment = (attachment: CoreoRecordAttachment): CoreoRecordAttachment | null => {
  // Drop attachments that cannot be displayed or re-uploaded (the #5602 ghost case).
  if (!attachment.fileLocation && !attachment.url) {
    return null;
  }

  return {
    ...attachment,
    id: nextClientAttachmentId(),
    // Local file: re-upload from disk (null url). Remote-only: keep url + size 0 so
    // uploadRecords uses uploadAttachmentClone and writes a new S3 key (#3826).
    url: attachment.fileLocation ? null : attachment.url,
    size: 0
  };
};

export const buildRecordClone = (
  toClone: CoreoRecord,
  date: string,
  userId: number,
  state: number,
  attributes: CoreoAttribute[] = []
): CoreoRecord => {
  const questionTypeByAttributeId = new Map(
    attributes.map(attribute => [attribute.id, attribute.questionType])
  );

  // If we clone a draft, make that a draft as well
  const syncState = toClone.syncState === CoreoRecordSyncStatus.DRAFT ? CoreoRecordSyncStatus.DRAFT : CoreoRecordSyncStatus.PENDING_UPDATE;

  const clone = (record: CoreoRecord): CoreoRecord => {
    const result: CoreoRecord = {
      ...record,
      id: null,
      syncState,
      createdAt: date,
      updatedAt: date,
      userId,
      deletedAt: null,
      state: state,
      associates: {},
      attachments: record.attachments
        .map(cloneRecordAttachment)
        .filter((attachment): attachment is CoreoRecordAttachment => attachment !== null)
    };

    for (const key in record.associates) {
      const attributeId = Number(key);
      const questionType = questionTypeByAttributeId.get(attributeId);
      result.associates[key] = [];
      for (const a of record.associates[key]) {
        if (questionType === 'association') {
          result.associates[key].push({ ...a });
        } else {
          result.associates[key].push(clone(a));
        }
      }
    }
    return result;
  };
  return clone(toClone);
};
